import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Methodology } from '../../types';

// Helper function to transform _id to id
const transformMethodology = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map(item => transformMethodology(item));
  }
  if (data && typeof data === 'object') {
    if ('_id' in data) {
      const { _id, ...rest } = data;
      return { id: _id, ...rest };
    }
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, transformMethodology(value)])
    );
  }
  return data;
};

export const methodologyApi = createApi({
  reducerPath: 'methodologyApi',
  baseQuery: fetchBaseQuery({ 
    baseUrl: '/is/methodology',
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as { auth: { token: string | null } };
      const token = state.auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Methodology'],
  endpoints: (builder) => ({
    // Get all methodologies
    getMethodologies: builder.query<Methodology[], void>({
      query: () => '',
      transformResponse: (response: { data: any[] }) => 
        transformMethodology(response.data),
      providesTags: ['Methodology'],
    }),

    // Get methodology by ID
    getMethodologyById: builder.query<Methodology, string>({
      query: (id) => `/${id}`,
      transformResponse: (response: { data: any }) => 
        transformMethodology(response.data),
      providesTags: (_result, _error, id) => [{ type: 'Methodology', id }],
    }),

    // Get methodologies by domain
    getMethodologiesByDomain: builder.query<Methodology[], string>({
      query: (domain) => `/domain/${domain}`,
      transformResponse: (response: { data: any[] }) => 
        transformMethodology(response.data),
      providesTags: ['Methodology'],
    }),

    // Add a new methodology
    addMethodology: builder.mutation<Methodology, Partial<Methodology>>({
      query: (methodology) => ({
        url: '',
        method: 'POST',
        body: methodology,
      }),
      transformResponse: (response: { data: any }) => 
        transformMethodology(response.data),
      invalidatesTags: ['Methodology'],
    }),

    // Update a methodology
    updateMethodology: builder.mutation<Methodology, Partial<Methodology> & { id: string }>({
      query: ({ id, ...update }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: update,
      }),
      transformResponse: (response: { data: any }) => 
        transformMethodology(response.data),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Methodology', id }],
    }),

    // Delete a methodology
    deleteMethodology: builder.mutation<{ success: boolean; id: string }, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Methodology', id }],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetMethodologiesQuery,
  useGetMethodologyByIdQuery,
  useGetMethodologiesByDomainQuery,
  useAddMethodologyMutation,
  useUpdateMethodologyMutation,
  useDeleteMethodologyMutation,
} = methodologyApi;
